import { ChecklistAssigneeType, ID, MEMBERS_APP_SLUG, Member, Team } from "@smartsuite/types";
import { useCallback, useMemo } from "react";
import { useQuery } from "react-query";

import {
  getChecklistAssigneeMembers,
  getChecklistAssigneeTeams,
  getInternalChecklistAssigneeMembers,
  getInternalChecklistAssigneeTeams,
} from "../../../api";
import { useFormContext } from "../../../hooks/useFormContext";
import { useIsPreview } from "../../../hooks/useIsPreview";
import { Fields } from "../../../types/fields";
import { Member as FormMember, Team as FormTeam } from "../../../types/form";

/**
 * The forms member endpoint returns only `{ id, name }`, so the lightweight form member is
 * adapted into the minimal shape react-ui's consumers read: `memberDisplayName` takes
 * `full_name.sys_root`, and `memberInitials` the first/last name — which the endpoint does not
 * split, so we do.
 *
 * `application_slug` is what makes `isMember` in fields-logic recognise this as a member.
 * Without it `memberInitials` falls through to its non-member branch and splits a top-level
 * `first_name` that a member record has never carried, throwing and taking the whole field
 * down with it.
 */
const adaptMember = (member: FormMember): Member => {
  const [firstName = "", ...restOfName] = member.name.trim().split(/\s+/);

  return {
    id: member.id,
    application_slug: MEMBERS_APP_SLUG,
    full_name: {
      sys_root: member.name,
      first_name: firstName,
      last_name: restOfName.join(" "),
    },
  } as unknown as Member;
};

/**
 * The forms team endpoint returns the same lightweight `{ id, name, color }` the Team field
 * uses; react-ui's team assignee picker only reads those three, so the rest of the platform
 * `Team` model is not fetched.
 */
const adaptTeam = (team: FormTeam): Team =>
  ({
    id: team.id,
    name: team.name,
    color: team.color,
  } as unknown as Team);

export interface UseChecklistAssigneesArgs {
  fieldSlug: string;
  /**
   * Skips both requests while false. Cached data is still returned, which is what lets the
   * review page read the names the input control already fetched instead of asking again.
   */
  enabled?: boolean;
}

export interface UseChecklistAssigneesResult {
  members: Member[];
  teams: Team[];
  isTeamAssignmentEnabled: boolean;
  /** Resolves an item's `assignee` to a display name; "" when the id is unknown to this form. */
  getAssigneeName: (assignee?: ID | null, assigneeType?: ChecklistAssigneeType | null) => string;
}

/**
 * Fetches the members and teams a checklist item may be assigned to. Shared by the input
 * control and the review page — the query keys are deliberately identical so the review page
 * is a cache hit for a field the respondent has already filled in.
 */
export const useChecklistAssignees = ({
  fieldSlug,
  enabled = true,
}: UseChecklistAssigneesArgs): UseChecklistAssigneesResult => {
  const { isPreview } = useIsPreview();
  const { accountId, config, passcode, isInternal, reportId } = useFormContext();

  // Members assignable to a checklist item: every active, non-system member with access to
  // the table (the backend applies that filter). Degrades to an empty list so a failure here
  // never takes the form down — the item text and completion checkbox still work.
  const { data: membersData } = useQuery(
    `${Fields.checklistfield}_${fieldSlug}`,
    isInternal && reportId
      ? () => getInternalChecklistAssigneeMembers(accountId, reportId, fieldSlug)
      : async () => {
          if (accountId && config.sharing_hash && fieldSlug) {
            return await getChecklistAssigneeMembers(
              accountId,
              config.sharing_hash,
              fieldSlug,
              isPreview,
              passcode
            );
          }
          return [];
        },
    {
      staleTime: Infinity,
      retry: false,
      enabled,
    }
  );

  // A checklist item's assignee can hold a team id too, but only behind this flag. Gate the
  // request on it so a form never pays for a call whose result it must not offer.
  const isTeamAssignmentEnabled = config.feature_flags?.checklist_team_assignment === true;

  const { data: teamsData } = useQuery(
    `${Fields.checklistfield}_teams_${fieldSlug}`,
    isInternal && reportId
      ? () => getInternalChecklistAssigneeTeams(accountId, reportId, fieldSlug)
      : async () => {
          if (accountId && config.sharing_hash && fieldSlug) {
            return await getChecklistAssigneeTeams(
              accountId,
              config.sharing_hash,
              fieldSlug,
              isPreview,
              passcode
            );
          }
          return [];
        },
    {
      staleTime: Infinity,
      retry: false,
      enabled: enabled && isTeamAssignmentEnabled,
    }
  );

  const members = useMemo<Member[]>(() => (membersData ?? []).map(adaptMember), [membersData]);
  const teams = useMemo<Team[]>(() => (teamsData ?? []).map(adaptTeam), [teamsData]);

  const memberNames = useMemo(
    () => new Map((membersData ?? []).map((member) => [member.id, member.name])),
    [membersData]
  );
  const teamNames = useMemo(
    () => new Map((teamsData ?? []).map((team) => [team.id, team.name])),
    [teamsData]
  );

  // An absent `assignee_type` means member, for back-compat with items written before teams
  // could be assigned.
  const getAssigneeName = useCallback(
    (assignee?: ID | null, assigneeType?: ChecklistAssigneeType | null) => {
      if (!assignee) return "";
      const names = assigneeType === "team" ? teamNames : memberNames;

      return names.get(String(assignee)) ?? "";
    },
    [memberNames, teamNames]
  );

  return {
    members,
    teams,
    isTeamAssignmentEnabled,
    getAssigneeName,
  };
};
